import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Tabs,
  Modal,
  message
} from 'antd';
import { 
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminAcademyApi, academyTeacherApi } from '@/services/api';
import { BasicInfo, TeacherInfo, PaymentHistory } from '@/pages/admin/components/academy';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface AcademyDetail {
  id: string;
  academyName: string;
  loginId: string;
  representativeName: string;
  representativePhone: string;
  representativeEmail: string;
  businessNumber: string;
  academyCode: string;
  totalUsers: number;
  activeUsers: number;
  teacherCount: number;
  academyUrl: string;
  logo: string;
  seatRange?: string;
  registrationStatus: string;
  serviceFeePayment: string;
  serviceFeePaymentDate: string;
  contractStatus: string;
  contractStatusKey?: string;
  contractStatusLabel?: string;
  overdueCount?: number;
  overdueAmount?: number;
  hasOverduePayments?: boolean;
  hasRefundableExamTickets?: boolean;
  refundableExamTicketCount?: number;
  applicationDate: string;
  registrationDate: string;
  withdrawalDate?: string;
}

interface TeacherData {
  key: string;
  no: number;
  teacherId: string;
  name: string;
  birthDate: string;
  status: 'normal' | 'withdrawn';
  registrationDate: string;
}

const AcademyDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { academyCode } = useParams<{ academyCode: string }>();
  const fromPath = (location.state as { from?: string } | null)?.from;
  const [loading, setLoading] = useState(true);
  const [academyData, setAcademyData] = useState<AcademyDetail | null>(null);
  const [teachers, setTeachers] = useState<TeacherData[]>([]);
  
  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      if (academyCode) {
        try { localStorage.setItem('last_admin_academyCode', academyCode); } catch (e) {}
      }
      const res = await adminAcademyApi.detail(academyCode || '');
      const data = res?.data || res;
      setAcademyData({
        id: data.loginId || '',
        academyName: data.academyName,
        loginId: data.loginId || '-',
        representativeName: data.representativeName,
        representativePhone: data.representativePhone,
        representativeEmail: data.representativeEmail,
        businessNumber: data.businessNumber,
        academyCode: data.academyCode,
        totalUsers: data.totalUsers || 0,
        activeUsers: data.activeUsers || 0,
        teacherCount: data.teacherCount || 0,
        academyUrl: data.academyUrl,
        logo: data.logoUrl,
        seatRange: data.seatRange || '',
        registrationStatus: data.registrationStatus,
        serviceFeePayment: data.serviceFeePayment,
        serviceFeePaymentDate: data.serviceFeePaymentDate,
        contractStatus: data.contractStatus,
        contractStatusKey: data.contractStatusKey,
        contractStatusLabel: data.contractStatusLabel,
        overdueCount: data.overdueCount ?? 0,
        overdueAmount: data.overdueAmount ?? 0,
        hasOverduePayments: data.hasOverduePayments ?? (data.contractStatusKey === 'payment_failed'),
        hasRefundableExamTickets: data.hasRefundableExamTickets ?? false,
        refundableExamTicketCount: data.refundableExamTicketCount ?? 0,
        applicationDate: data.applicationDate,
        registrationDate: data.registrationDate,
        withdrawalDate: data.withdrawalDate,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [academyCode]);

  const handleWithdrawFlow = useCallback(() => {
    if (!academyData?.academyCode) {
      message.warning('학원 정보를 불러올 수 없습니다.');
      return;
    }
    const code = academyData.academyCode;
    const isOverdue = Boolean(academyData.hasOverduePayments);
    const hasRefundable = Boolean(academyData.hasRefundableExamTickets);

    const runBillingHold = async () => {
      if (academyData.contractStatusKey === 'billing_hold') {
        return;
      }
      await adminAcademyApi.holdBilling(code);
      message.success('차월 정기결제가 중지되었습니다.');
      await fetchDetail();
    };

    const showInfoModal = (lines: string[]) => {
      Modal.info({
        title: '탈퇴 처리가 제한되었습니다.',
        content: (
          <div>
            {lines.map((line, idx) => (
              <p key={idx} style={{ marginBottom: 4 }}>{line}</p>
            ))}
          </div>
        ),
        okText: '확인',
      });
    };

    const handleBlockingCase = (lines: string[]) => {
      (async () => {
        try {
          await runBillingHold();
        } catch (err: any) {
          message.error(err?.response?.data?.message || '유예기간 처리에 실패했습니다.');
        } finally {
          showInfoModal(lines);
        }
      })();
    };

    if (!isOverdue && !hasRefundable) {
      Modal.confirm({
        title: '탈퇴 처리',
        content: (
          <>
            <p>탈퇴 처리하시겠습니까?</p>
            <p>남은 서비스 이용기간까지는 정상 이용 가능합니다.</p>
          </>
        ),
        okText: '탈퇴 처리',
        cancelText: '취소',
        onOk: async () => {
          try {
            await adminAcademyApi.withdraw(code);
            message.success('탈퇴 처리가 완료되었습니다.');
            await fetchDetail();
          } catch (err: any) {
            message.error(err?.response?.data?.message || '탈퇴 처리에 실패했습니다.');
            throw err;
          }
        },
      });
      return;
    }

    if (!isOverdue && hasRefundable) {
      handleBlockingCase([
        '환불 가능한 시험 응시권이 존재하며 처리할 수 없습니다.',
        '환불 처리 안내 및 완료 후 다시 시도해 주세요.',
        '차월 정기결제는 중지됩니다.'
      ]);
      return;
    }

    if (isOverdue && hasRefundable) {
      handleBlockingCase([
        '미납내역과 환불 가능한 시험 응시권이 존재하여 처리할 수 없습니다.',
        '응시권 환불 및 미납내역 결제 후 다시 시도해 주세요.',
        '차월 정기결제는 중지됩니다.'
      ]);
      return;
    }

    handleBlockingCase([
      '미납내역이 있어 처리할 수 없습니다.',
      '결제 안내 및 완료 후 다시 시도해 주세요.',
      '차월 정기결제는 중지됩니다.'
    ]);
  }, [academyData, fetchDetail]);
  
  // BasicInfo 저장 이벤트 처리 및 탈퇴 컨트롤
  useEffect(() => {
    const onSave = async (e: any) => {
      try {
        const payload = e.detail || {};
        await adminAcademyApi.updateBasic(academyCode || '', {
          academyName: payload.academyName,
          representativeName: payload.representativeName,
          representativePhone: payload.representativePhone,
          representativeEmail: payload.representativeEmail,
          academyUrl: payload.academyUrl,
          logoUrl: payload.logoUrl ?? (Array.isArray(payload.logo) ? (payload.logo[0]?.url || '') : payload.logo),
          serviceFeePayment: payload.serviceFeePayment,
          serviceFeePaymentDate: payload.serviceFeePaymentDate,
          applicationDate: payload.applicationDate,
          registrationDate: payload.registrationDate,
          registrationStatus: undefined,
          contractStatus: undefined,
        });
        message.success('저장되었습니다.');
        await fetchDetail();
      } catch (err: any) {
        message.error(err?.response?.data?.message || '저장 중 오류');
      }
    };

    window.addEventListener('admin:academy:save-basic', onSave as EventListener);
    return () => {
      window.removeEventListener('admin:academy:save-basic', onSave as EventListener);
    };
  }, [academyCode, fetchDetail]);

  useEffect(() => {
    const onWithdraw = () => {
      handleWithdrawFlow();
    };
    window.addEventListener('admin:academy:withdraw', onWithdraw as EventListener);
    return () => {
      window.removeEventListener('admin:academy:withdraw', onWithdraw as EventListener);
    };
  }, [handleWithdrawFlow]);
  
  // URL에서 tab 파라미터 읽기
  const urlParams = new URLSearchParams(window.location.search);
  const activeTab = urlParams.get('tab') || 'basic';

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // 강사 목록 로드
  const fetchTeachers = async () => {
    if (!academyCode) return;
    try {
      const res = await academyTeacherApi.list({ academyCode, page: 0, size: 100 });
      const pageData = res?.data || res;
      const content = pageData?.content || [];
      const mapped: TeacherData[] = content.map((it: any, idx: number) => ({
        key: String(it.id),
        no: idx + 1,
        teacherId: String(it.id),
        name: it.name,
        birthDate: it.birthDate || '-',
        status: it.status === '정상' ? 'normal' : 'withdrawn',
        registrationDate: it.registrationDate || '-',
      }));
      setTeachers(mapped);
    } catch (e) {
      console.error(e);
      setTeachers([]);
    }
  };

  useEffect(() => {
    if (activeTab === 'teachers') {
      fetchTeachers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, academyCode]);

  if (loading) {
    return (
      <AdminLayout>
        <div>로딩 중...</div>
      </AdminLayout>
    );
  }

  if (!academyData) {
    return (
      <AdminLayout>
        <div>학원 정보를 찾을 수 없습니다.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(fromPath || '/admin/academy/list')}
            >
              목록으로
            </Button>
          </Space>
          
          <Title level={2}>{academyData.academyName}</Title>
          <Text type="secondary">학원ID: {academyData.loginId}</Text>
        </div>

        {/* 상세 정보 */}
        <Card>
          <Tabs defaultActiveKey={activeTab} onChange={(k) => { if (k === 'teachers') fetchTeachers(); }}>
            <TabPane tab="기본정보" key="basic">
              <BasicInfo academyData={academyData} />
            </TabPane>
            <TabPane tab="강사정보" key="teachers">
              <TeacherInfo teachers={teachers} />
            </TabPane>
            <TabPane tab="결제이력" key="payments">
              <PaymentHistory academyCode={academyData.academyCode} />
            </TabPane>
          </Tabs>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AcademyDetail; 